/**
 * useMapData Hook
 * 
 * Fetches all opportunities with coordinates across all portfolios.
 * Manages map state (selected opportunity, filters, basemap).
 * Persists user's map position for seamless navigation.
 * Encapsulates API calls and map logic per Rule 6.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import { OpportunityStatus } from '../shared/types/opportunity';

// =============================================================================
// Types
// =============================================================================

export interface MapOpportunity {
  id: number;
  portfolio_id: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  county: string | null;
  latitude: number;
  longitude: number;
  status: OpportunityStatus;
  asking_price: number | null;
  acreage: number | null;
  priority: string | null;
  // Parcel data (from linked parcel or county records)
  property_value: number | null;  // Tax assessed value
  owner_name: string | null;
  parcel_id: string | null;       // County parcel ID
}

export type MapFilter = 'all' | OpportunityStatus;
export type BasemapStyle = 'outdoors' | 'streets' | 'satellite' | 'satellite-streets' | 'light' | 'dark';

interface UseMapDataReturn {
  opportunities: MapOpportunity[];
  filteredOpportunities: MapOpportunity[];
  selectedOpportunity: MapOpportunity | null;
  loading: boolean;
  error: string | null;
  filter: MapFilter;
  basemap: BasemapStyle;
  mapboxToken: string | null;
  setFilter: (filter: MapFilter) => void;
  setBasemap: (style: BasemapStyle) => void;
  selectOpportunity: (opp: MapOpportunity | null) => void;
  refresh: () => Promise<void>;
  mapCenter: [number, number];
  mapZoom: number;
  setMapPosition: (center: [number, number], zoom: number) => void;
}

// =============================================================================
// Session Storage for position persistence
// =============================================================================

const STORAGE_KEY = 'mapPosition';

function getStoredPosition(): { center: [number, number]; zoom: number } | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.center && typeof parsed.zoom === 'number') {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function storePosition(center: [number, number], zoom: number): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ center, zoom }));
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// API URL helper
// =============================================================================

const getApiBaseUrl = () => {
  if (process.env.EXPO_PUBLIC_API_URL) return process.env.EXPO_PUBLIC_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return 'https://api.farmerjohn.pacayagroup.com';
  }
  return 'http://localhost:3001';
};

// =============================================================================
// Hook
// =============================================================================

export function useMapData(): UseMapDataReturn {
  const [opportunities, setOpportunities] = useState<MapOpportunity[]>([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState<MapOpportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<MapFilter>('all');
  const [basemap, setBasemap] = useState<BasemapStyle>('outdoors');
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  
  // User's manual position (persisted in session storage)
  const [userPosition, setUserPosition] = useState<{ center: [number, number]; zoom: number } | null>(
    getStoredPosition
  );
  const hasInitializedPosition = useRef(false);

  // Fetch Mapbox token
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const envToken = process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN;
        if (envToken) {
          setMapboxToken(envToken);
          return;
        }
        const response = await fetch(`${getApiBaseUrl()}/api/config/public`);
        if (response.ok) {
          const config = await response.json();
          setMapboxToken(config.mapbox?.accessToken || null);
        }
      } catch (err) {
        logger.warn('Failed to fetch Mapbox token', 'map');
      }
    };
    fetchToken();
  }, []);

  // Fetch all portfolios and their opportunities
  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const portfoliosResponse = await authApi.getPortfolios();
      if (!portfoliosResponse.success || !portfoliosResponse.portfolios) {
        setOpportunities([]);
        return;
      }

      const allOpportunities: MapOpportunity[] = [];
      
      for (const portfolio of portfoliosResponse.portfolios) {
        try {
          const oppResponse = await authApi.getOpportunities(portfolio.id);
          if (oppResponse.success && oppResponse.opportunities) {
            const mappable = oppResponse.opportunities
              .filter((opp: any) => opp.latitude && opp.longitude)
              .map((opp: any) => ({
                id: opp.id,
                portfolio_id: portfolio.id,
                name: opp.name || opp.street_address || `Opportunity ${opp.id}`,
                address: opp.street_address,
                city: opp.city,
                state: opp.state,
                county: opp.county,
                latitude: opp.latitude,
                longitude: opp.longitude,
                status: opp.status,
                asking_price: opp.asking_price,
                acreage: opp.acreage,
                priority: opp.priority,
                // Parcel data from county records
                property_value: opp.property_value,  // Tax assessed value
                owner_name: opp.owner_name,
                parcel_id: opp.parcel_id,
              }));
            allOpportunities.push(...mappable);
          }
        } catch (portfolioErr) {
          logger.warn(`Failed to fetch opportunities for portfolio ${portfolio.id}`, 'map');
        }
      }

      setOpportunities(allOpportunities);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load map data';
      logger.error('Map data fetch failed', 'map', String(err));
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  // Filter opportunities based on selected filter
  const filteredOpportunities = useMemo(() => {
    if (filter === 'all') return opportunities;
    return opportunities.filter(opp => opp.status === filter);
  }, [opportunities, filter]);

  // Calculate default position based on opportunities
  const defaultPosition = useMemo(() => {
    const opps = filteredOpportunities.length > 0 ? filteredOpportunities : opportunities;
    
    if (opps.length === 0) {
      return { center: [-98.5795, 39.8283] as [number, number], zoom: 4 };
    }

    if (opps.length === 1) {
      return { 
        center: [opps[0].longitude, opps[0].latitude] as [number, number], 
        zoom: 12 
      };
    }

    const lngs = opps.map(o => o.longitude);
    const lats = opps.map(o => o.latitude);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const centerLng = (minLng + maxLng) / 2;
    const centerLat = (minLat + maxLat) / 2;

    const maxDiff = Math.max(maxLng - minLng, maxLat - minLat);
    let zoom = 10;
    if (maxDiff < 0.01) zoom = 14;
    else if (maxDiff < 0.05) zoom = 12;
    else if (maxDiff < 0.1) zoom = 11;
    else if (maxDiff < 0.5) zoom = 9;
    else if (maxDiff < 1) zoom = 8;
    else if (maxDiff < 5) zoom = 6;
    else zoom = 4;

    return { center: [centerLng, centerLat] as [number, number], zoom };
  }, [filteredOpportunities, opportunities]);

  // Use stored position if available, otherwise calculate from opportunities
  const { mapCenter, mapZoom } = useMemo(() => {
    // If user has manually panned/zoomed, use that position
    if (userPosition) {
      return { mapCenter: userPosition.center, mapZoom: userPosition.zoom };
    }
    return { mapCenter: defaultPosition.center, mapZoom: defaultPosition.zoom };
  }, [userPosition, defaultPosition]);

  // Update user position when they pan/zoom
  const setMapPosition = useCallback((center: [number, number], zoom: number) => {
    // Only start tracking after initial load
    if (!hasInitializedPosition.current) {
      hasInitializedPosition.current = true;
      return;
    }
    setUserPosition({ center, zoom });
    storePosition(center, zoom);
  }, []);

  const selectOpportunity = useCallback((opp: MapOpportunity | null) => {
    setSelectedOpportunity(opp);
  }, []);

  return {
    opportunities,
    filteredOpportunities,
    selectedOpportunity,
    loading,
    error,
    filter,
    basemap,
    mapboxToken,
    setFilter,
    setBasemap,
    selectOpportunity,
    refresh: fetchOpportunities,
    mapCenter,
    mapZoom,
    setMapPosition,
  };
}
